import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Play, Download, Copy, Check, ArrowLeft, Film, MonitorPlay, Eye,
  Loader2, Share2, ExternalLink
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const STYLE_ICONS = { 'Screen Recording': MonitorPlay, 'Cinematic': Film, 'Slideshow': Eye };
const STYLE_COLORS = { 'Screen Recording': 'text-cyan-400', 'Cinematic': 'text-violet-400', 'Slideshow': 'text-amber-400' };
const STYLE_BG = { 'Screen Recording': 'bg-cyan-950/20 border-cyan-900/30', 'Cinematic': 'bg-violet-950/20 border-violet-900/30', 'Slideshow': 'bg-amber-950/20 border-amber-900/30' };

export default function VideoSharePage() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/demo/share/${slug}`)
      .then(r => { if (!r.ok) throw new Error('Video not found'); return r.json(); })
      .then(d => { setMeta(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [slug]);

  const shareUrl = `${BASE_URL}/demo/video/${slug}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !meta) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Video not found</p>
        <Link to="/demo" className="text-sm text-primary hover:text-primary flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to Demo
        </Link>
      </div>
    );
  }

  const Icon = STYLE_ICONS[meta.style] || Film;
  const color = STYLE_COLORS[meta.style] || 'text-muted-foreground';
  const bgBorder = STYLE_BG[meta.style] || 'bg-slate-950/20 border-border';

  return (
    <div className="min-h-screen bg-background" data-testid="video-share-page">
      {/* Header bar */}
      <div className="border-b border-border/50 bg-card/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/demo" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground/80 transition-colors" data-testid="back-to-demo">
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="font-heading uppercase tracking-wider">Back to Demo</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">FMiQ Intel</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Title section */}
        <div className="flex items-center gap-3 mb-6">
          <div className={`w-10 h-10 rounded-sm flex items-center justify-center border ${bgBorder}`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
          <div>
            <h1 className="text-xl font-heading font-bold text-foreground" data-testid="video-title">{meta.title}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{meta.description} &middot; {meta.size_mb} MB</p>
          </div>
        </div>

        {/* Video player */}
        <div className="bg-black rounded-sm overflow-hidden border border-border mb-6" data-testid="video-player-container">
          <video
            src={`${BASE_URL}${meta.stream_url}`}
            controls
            autoPlay
            className="w-full"
            data-testid="share-video-player"
          />
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={copyLink}
            data-testid="copy-share-link"
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-heading uppercase tracking-wider rounded-sm border transition-all ${
              copied
                ? 'bg-emerald-600/20 border-emerald-700 text-emerald-400'
                : 'bg-primary/10 border-primary/30/50 text-primary hover:bg-primary/15 hover:border-orange-800'
            }`}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Link Copied!' : 'Copy Share Link'}
          </button>

          <a
            href={`${BASE_URL}${meta.stream_url}`}
            download={meta.filename}
            data-testid="download-video"
            className="flex items-center gap-2 px-4 py-2.5 text-xs font-heading uppercase tracking-wider rounded-sm border bg-muted/50 border-border text-muted-foreground hover:text-foreground/80 hover:border-slate-600 transition-all"
          >
            <Download className="w-3.5 h-3.5" /> Download ({meta.size_mb} MB)
          </a>

          <button
            onClick={() => {
              if (navigator.share) {
                navigator.share({ title: meta.title, text: meta.description, url: shareUrl });
              } else {
                copyLink();
              }
            }}
            data-testid="native-share"
            className="flex items-center gap-2 px-4 py-2.5 text-xs font-heading uppercase tracking-wider rounded-sm border bg-muted/50 border-border text-muted-foreground hover:text-foreground/80 hover:border-slate-600 transition-all"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>

        {/* Share URL display */}
        <div className="p-4 bg-card border border-border rounded-sm" data-testid="share-url-box">
          <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-2">Shareable Link</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs font-mono text-muted-foreground bg-muted/50 px-3 py-2 rounded-sm border border-border/50 truncate" data-testid="share-url-text">
              {shareUrl}
            </code>
            <button
              onClick={copyLink}
              className="shrink-0 p-2 rounded-sm border border-border text-muted-foreground hover:text-foreground/80 hover:border-slate-600 transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Link
            to="/demo"
            data-testid="explore-demo-cta"
            className="inline-flex items-center gap-2 px-6 py-3 text-xs font-heading uppercase tracking-wider rounded-sm bg-orange-600 text-white hover:bg-primary transition-colors"
          >
            Explore the Full Demo <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
